/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      controller — online-payment status page and retry-payment flow
 *   extends:   Controller
 *   template:  none
 *   key:       isNotProjectPayed, pagamentoVerificato, outcome / isChecking / canRetry
 *              (getters, lib/payment-outcome), pollPaymentTask (re-reads the payment
 *              while the bank's answer is pending, sets verifyTimedOut when it gives
 *              up), retryPayment (task), sendInvitation (action)
 *   uses:      applicationController (app/controllers/application.js) —
 *              saveRecordRelated, sendInvitation; services: intl, notify,
 *              store, session, log; rsvp `all`, moment-timezone, lib/payment-outcome
 *   siblings:  routes/payment-status.js (starts pollPaymentTask on entry, cancels it
 *              on exit)
 *   tests:     tests/unit/controllers/payment-status-test.js,
 *              tests/unit/routes/payment-status-route-test.js,
 *              tests/acceptance/payment-status-test.js
 *   gotchas:   isNotProjectPayed compares `project_state.id` (an Ember Data
 *              string id) against the number `92` — always true regardless of
 *              real state; fixed to compare against the string `'92'`.
 *              retryPayment's `saveRecordRelated` never rejects (resolves
 *              `false` on failure), so its catch block never runs for a
 *              refused save — the task explicitly checks
 *              `paymentSaved === false` and rolls back the unsaved retry
 *              payment itself. `access_token` QP is owned by the application
 *              controller (see application.js queryParams) — do NOT redeclare
 *              it here, two controllers mapping the same QP key throws on
 *              route entry; the deep-link token is read in
 *              routes/payment-status.js beforeModel instead.
 *              No retry while the outcome is 'checking' (canRetry): the bank has not
 *              answered, a charge may be under way, a second payment would charge
 *              twice. pollPaymentTask uses rawTimeout (not the runloop timeout) so a
 *              test's settled() does not wait a minute for it; the route cancels it.
 *
 * Online-payment status/retry controller for the e-commerce storefront.
 */
import Controller from '@ember/controller';
import { all } from 'rsvp';
import moment from 'moment-timezone';

import { task, rawTimeout } from 'ember-concurrency';

import { action, set } from '@ember/object';
import { tracked } from '@glimmer/tracking';

import { inject as service } from '@ember/service';
import { inject as controller } from '@ember/controller';

import { isPaymentValidForRetry } from 'beebee-online-payments/lib/payment-logic';
import {
  PAYMENT_OUTCOME,
  PAYMENT_POLL_INTERVAL_MS,
  PAYMENT_POLL_MAX_ATTEMPTS,
  canRetryPayment,
  paymentOutcome,
} from 'e-commerce/lib/payment-outcome';

export default class PaymentStatusController extends Controller {
  @service intl;

  @service notify;
  @service store;
  @service session;
  @service log;
  @controller('application') applicationController;

  // NOTE: `access_token` QP is owned by the application controller (see
  // application.js `queryParams`). Do NOT redeclare it here — two active
  // controllers mapping the same QP key makes Ember throw on route entry.
  // The deep-link token is read in routes/payment-status.js beforeModel.

  get isNotProjectPayed() {
    // Ember Data ids are strings: `!== 92` (number) was always true, so this
    // getter never reported a project as paid regardless of its real state.
    return this.content?.projects?.[0]?.project_state?.id !== '92';
  }

  get pagamentoVerificato() {
    return !!this.content?.tran_bank_id && !!this.content?.transaction_type;
  }

  @tracked newPayment = null;

  // Set by pollPaymentTask when the bank's answer did not arrive within the poll window:
  // the page then warns and offers the retry (see lib/payment-outcome).
  @tracked verifyTimedOut = false;

  pollIntervalMs = PAYMENT_POLL_INTERVAL_MS;
  pollMaxAttempts = PAYMENT_POLL_MAX_ATTEMPTS;

  get isBonifico() {
    return this.content?.payment_method === 'Bonifico';
  }

  get outcome() {
    return paymentOutcome({
      success: !!this.content?.success,
      failed: this.pagamentoVerificato,
      bonifico: this.isBonifico,
      timedOut: this.verifyTimedOut,
    });
  }

  get isChecking() {
    return this.outcome === PAYMENT_OUTCOME.CHECKING;
  }

  get canRetry() {
    return canRetryPayment(this.outcome);
  }

  /**
   * While the outcome is 'checking', re-read the payment until the bank's answer lands
   * on it (success, or a verified failure) or the window closes; then `verifyTimedOut`
   * turns the page into the warned retry. Restartable: «check again» after a timeout
   * performs it anew. The payment id is captured before the first yield.
   */
  @task({ restartable: true }) *pollPaymentTask() {
    const id = this.content?.id;
    this.verifyTimedOut = false;
    if (!id) return;

    for (let attempt = 0; attempt < this.pollMaxAttempts; attempt++) {
      if (!this.isChecking) return;
      yield rawTimeout(this.pollIntervalMs);
      try {
        yield this.store.findRecord('payment', id, { reload: true });
      } catch (e) {
        // A failed read is one missed look, not an outcome: keep polling until the window closes.
        this.log.warn('payment-status: payment re-read failed', e);
      }
    }

    if (this.isChecking) this.verifyTimedOut = true;
  }

  @task({ drop: true }) *retryPayment() {
    if (!this.content?.id) return;
    // The template offers no retry while the bank is still answering; this keeps a stale
    // click (or a future template) from paying a second time anyway, and says why.
    if (this.isChecking) {
      this.notify.info(this.intl.t('ec.payment.checking_no_retry'));
      return;
    }
    let newPayment = null;
    try {
      const oldPayment = yield this.store.findRecord('payment', this.content?.id, { reload: true });
      const dateDiff = moment().diff(moment(oldPayment.date), 'minutes');

      if (dateDiff > 15) {
        this.notify.warning(this.intl.t('online_payment.time_expired'));
        return false;
      }

      const isValid = yield isPaymentValidForRetry(oldPayment);
      if (!isValid) {
        this.notify.warning(this.intl.t('online_payment.project_archived'));
        return false;
      }

      newPayment = this.store.createRecord('payment', {
        sent: false,
        date: moment().format('YYYY-MM-DDTHH:mmZ'),
        total: oldPayment.total,
        option_json: oldPayment.option_json,
        option: oldPayment.option,
        voucher: oldPayment.voucher,
        status: oldPayment.status,
      });

      const [oldProjects, oldEvents, oldContacts] = yield all([
        oldPayment.projects,
        oldPayment.events,
        oldPayment.contacts,
      ]);

      const [newProjects, newEvents, newContacts] = yield all([
        newPayment.projects,
        newPayment.events,
        newPayment.contacts,
      ]);

      for (const project of oldProjects.slice()) {
        newProjects.push(project);
      }
      for (const event of oldEvents.slice()) {
        newEvents.push(event);
      }
      for (const contact of oldContacts.slice()) {
        newContacts.push(contact);
      }

      const paymentSaved = yield this.applicationController.saveRecordRelated(newPayment);

      // saveRecordRelated NEVER rejects — it resolves `false` on failure, so the catch
      // below never fires for a refused save and its rollback never ran: the unsaved
      // retry `payment` stayed in the store as an isNew record, ready to be swept into
      // a later related save.
      if (paymentSaved === false) {
        if (!newPayment.isDestroyed && !newPayment.isDestroying) {
          newPayment.rollbackAttributes();
        }
        return;
      }

      if (paymentSaved) {
        //this.store.pushPayload(paymentSaved[0]);
        const savedId = paymentSaved[0]?.data?.id;
        if (savedId) {
          const storeItem = this.store.peekRecord('payment', savedId);
          set(this, 'newPayment', storeItem);
        }
      }
    } catch (error) {
      // Roll back the unsaved retry payment so it doesn't orphan in the store.
      if (newPayment && !newPayment.isDestroyed && !newPayment.isDestroying) {
        newPayment.rollbackAttributes();
      }
      this.log.error('Error retrying payment:', error);
      this.notify.error(this.intl.t('beebeeboard error'));
    }
  }

  @action
  sendInvitation() {
    this.applicationController.sendInvitation.perform();
  }
}
